import type { TypedFormFieldConfiguration } from '@risksmart-app/form-configuration/src/field-service';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type {
  FormEditorInitialData,
  FormEditorOutput,
} from 'src/blocks/form-editor-dialog';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { FormConfigurationUpdatePayload } from './buildFormConfigurationUpdatePayload';
import { useFormEditorUpdateGraphQL } from './useFormEditorUpdateGraphQL';
import { useFormEditorUpdateTRPC } from './useFormEditorUpdateTRPC';

interface UseFormEditorUpdateOptions {
  parentType: FormId;
  initialData: FormEditorInitialData;
  customAttributeSchema: FormConfigurationUpdatePayload['CustomAttributeSchema'];
  formFieldConfigurations: TypedFormFieldConfiguration[] | null;
}

/**
 * Public, flag-gated bulk save hook for the form editor. Reads the `trpc`
 * feature flag and delegates to either the GraphQL/Hasura path (default) or
 * the tRPC path. Both produce identical DB state via the shared
 * `buildFormConfigurationUpdatePayload` transform.
 */
export const useFormEditorUpdate = (
  options: UseFormEditorUpdateOptions
): ((output: FormEditorOutput) => Promise<void>) => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const graphqlSave = useFormEditorUpdateGraphQL(options);
  const trpcSave = useFormEditorUpdateTRPC(options);

  return trpcEnabled ? trpcSave : graphqlSave;
};
